(function () {
  const ROOT = "[data-adaptive-image-slider]";
  const DESKTOP_MIN = 900;

  function debounce(fn, wait) {
    let timeoutId;
    return function debounced(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function clearInlineSlideWidths(swiper) {
    if (!swiper?.slides?.length) return;
    swiper.slides.forEach((slide) => {
      slide.style.width = "";
    });
  }

  function getSlideKey(slide) {
    if (slide.dataset.slideIndex !== undefined) {
      return `index:${slide.dataset.slideIndex}`;
    }
    const img = slide.querySelector(".adaptive-image-slider__img");
    if (img?.currentSrc) return `src:${img.currentSrc}`;
    if (img?.src) return `src:${img.src}`;
    return null;
  }

  function measureSlideWidth(slide) {
    const inner = slide.querySelector(".adaptive-image-slider__inner");
    if (!inner) return 0;
    slide.style.width = "";
    return Math.ceil(inner.getBoundingClientRect().width);
  }

  function syncSlideWidths(swiper) {
    if (!swiper?.slides?.length || window.innerWidth < DESKTOP_MIN) return;

    const widthByKey = new Map();

    swiper.slides.forEach((slide) => {
      const key = getSlideKey(slide);
      if (!key || widthByKey.has(key)) return;
      widthByKey.set(key, measureSlideWidth(slide));
    });

    swiper.slides.forEach((slide) => {
      const key = getSlideKey(slide);
      const width = key && widthByKey.has(key) ? widthByKey.get(key) : measureSlideWidth(slide);
      if (width > 0) slide.style.width = `${width}px`;
    });
  }

  function runLayout(swiper, { updateSwiper = true } = {}) {
    if (!swiper || swiper.destroyed || swiper.animating) return;

    if (window.innerWidth < DESKTOP_MIN) {
      clearInlineSlideWidths(swiper);
    } else {
      syncSlideWidths(swiper);
    }

    if (updateSwiper) {
      swiper.updateSize();
    }
  }

  function bindSlider(root) {
    if (root.dataset.aisBound === "true") return true;

    const swiperEl = root.querySelector("[data-swiper]");
    const swiper = swiperEl?.swiper;
    if (!swiper) return false;

    root.dataset.aisBound = "true";

    const setTransitioning = (isActive) => {
      root.classList.toggle("adaptive-image-slider--transitioning", isActive);
      swiperEl.classList.toggle("adaptive-image-slider--transitioning", isActive);
    };

    const layout = () => runLayout(swiper);
    const layoutDebounced = debounce(() => runLayout(swiper), 200);
    const layoutFromImages = debounce(() => {
      if (!swiper.animating) runLayout(swiper);
    }, 200);

    requestAnimationFrame(() => {
      runLayout(swiper);
      if (swiper.params.loop) {
        requestAnimationFrame(() => {
          if (!swiper.destroyed) swiper.loopFix();
        });
      }
    });

    swiper.on("init", () => {
      requestAnimationFrame(() => {
        runLayout(swiper);
        if (swiper.params.loop && !swiper.destroyed) swiper.loopFix();
      });
    });

    swiper.on("resize", layoutDebounced);

    swiper.on("slideChangeTransitionStart", () => {
      setTransitioning(true);
    });

    swiper.on("slideChangeTransitionEnd", () => {
      setTransitioning(false);
    });

    root.querySelectorAll(".adaptive-image-slider__img").forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", layoutFromImages, { once: true });
    });

    if (window.Shopify?.designMode) {
      const host = root.closest("[data-section-id]");
      if (host) {
        const onSettingsChange = debounce(() => {
          if (!swiper.animating) runLayout(swiper);
        }, 200);
        const observer = new MutationObserver(onSettingsChange);
        observer.observe(host, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      }
    }

    return true;
  }

  function watchSlider(root) {
    if (bindSlider(root)) return;

    const observer = new MutationObserver(() => {
      if (bindSlider(root)) observer.disconnect();
    });

    observer.observe(root, { childList: true, subtree: true });

    window.setTimeout(() => {
      bindSlider(root);
      observer.disconnect();
    }, 2000);
  }

  function initSection(container) {
    if (!container) return;
    const root = container.matches?.(ROOT)
      ? container
      : container.querySelector?.(ROOT);
    if (root) watchSlider(root);
  }

  function init() {
    document.querySelectorAll(ROOT).forEach(watchSlider);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target?.querySelector?.(ROOT);
    if (root) delete root.dataset.aisBound;
  });
})();
